import { Capacitor } from "@capacitor/core";
import api from "@/modules/shared/api/axios";

// Browser-side failures (a screen that crashed while rendering, an uncaught error, an
// unhandled promise) are posted to the backend, which forwards them to Tarh Portal as
// incidents. Reporting is best-effort: it never throws, never retries, and is capped so a
// loop of errors cannot flood the API.

const MAX_PER_MINUTE = 5;
const CHUNK_PATTERN =
  /Loading (CSS )?chunk|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i;
const IGNORED_PATTERN = /ResizeObserver loop|Script error\.?$|__firefox__|zaloJSV2|AbortError/i;

const sentFingerprints = new Set();
let windowStartedAt = 0;
let sentInWindow = 0;

const APP_VERSION = import.meta.env.VITE_APP_VERSION || "";

function withinBudget() {
  const now = Date.now();
  if (now - windowStartedAt > 60_000) {
    windowStartedAt = now;
    sentInWindow = 0;
  }
  if (sentInWindow >= MAX_PER_MINUTE) return false;
  sentInWindow += 1;
  return true;
}

function describe(error) {
  if (error instanceof Error) {
    return {
      title: error.message || error.name || "Error",
      message: `${error.name || "Error"}: ${error.message || ""}`.trim(),
      stack: typeof error.stack === "string" ? error.stack : "",
    };
  }
  if (typeof error === "string") {
    return { title: error, message: error, stack: "" };
  }
  let text = "";
  try {
    text = JSON.stringify(error);
  } catch (_) {
    text = String(error);
  }
  return { title: text || "Unknown error", message: text, stack: "" };
}

// The first app frame of a React component stack, e.g. "TenderTable".
export function componentFromStack(componentStack) {
  if (typeof componentStack !== "string") return "";
  // Frames look like "    at TenderTable (...)" in production and "    in TenderTable" in dev.
  const match = componentStack.match(/^\s+(?:at|in)\s+([A-Z][A-Za-z0-9_$]*)/m);
  return match ? match[1] : "";
}

/**
 * Report one browser failure. `kind` is "render" (error boundary), "runtime"
 * (window.onerror), "promise" (unhandledrejection) or "chunk" (stale deploy).
 */
export function reportClientIncident(error, { kind = "runtime", component = "" } = {}) {
  try {
    const { title, message, stack } = describe(error);
    if (!title || IGNORED_PATTERN.test(title)) return;

    const finalKind = CHUNK_PATTERN.test(title) ? "chunk" : kind;
    const fingerprint = `${finalKind}|${title}|${stack.split("\n")[1] || ""}`;
    if (sentFingerprints.has(fingerprint)) return;
    if (!withinBudget()) return;
    sentFingerprints.add(fingerprint);

    api
      .post(
        "/client-incidents",
        {
          title: title.slice(0, 255),
          message: message.slice(0, 4000),
          stack: stack.slice(0, 6000),
          severity: finalKind === "chunk" ? "warning" : "error",
          kind: finalKind,
          url: typeof window !== "undefined" ? window.location.href.slice(0, 500) : "",
          component: (component || componentFromStack(stack)).slice(0, 120),
          platform: Capacitor.isNativePlatform() ? Capacitor.getPlatform() : "web",
          app_version: APP_VERSION.slice(0, 40),
        },
        { forceRefresh: true, timeout: 5000 }
      )
      .catch(() => {});
  } catch (_) {
    // Reporting must never add a second error on top of the first.
  }
}

/**
 * Catch what React cannot: errors outside the render tree and promises nobody awaited.
 * API failures are left out: the backend already reports its own server errors, and a
 * rejected request the screen handles is not a browser bug.
 */
export function installClientIncidentHandlers() {
  if (typeof window === "undefined" || window.__tarhIncidentHandlers) return;
  window.__tarhIncidentHandlers = true;

  window.addEventListener("error", (event) => {
    // Resource load failures (an <img> or <script> that 404s) arrive here without an error.
    if (!event?.error && event?.target && event.target !== window) return;
    reportClientIncident(event.error || event.message, { kind: "runtime" });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event?.reason;
    if (reason?.isAxiosError || reason?.name === "CanceledError") return;
    reportClientIncident(reason ?? "Unhandled promise rejection", { kind: "promise" });
  });
}
